// The older fee reserve notice (docs/18-one-fee-reserve.md section 6, "Explicit, not automatic"; section 10, Vault and
// Settings). On a legacy device, one that still stores the sealed per-device key of an earlier release, that key may
// hold SOL. Home and Settings show this notice whenever its balance reads above 0, at every visit: SOL can reach the
// old address later, and the notice returns then. After a move it clears when the balance reads 0.
// - Reading never opens the key: `engine.olderReserve()` reads both balances, F and the guard by the recorded address,
//   after the network check (2.5). A device set up before R48 has no recorded address; the notice then offers "Open it
//   to see its balance" (`engine.openOlderReserve`), which records the address through the session's compare-and-set
//   writer.
// - Move reads everything again and shows one review panel: the amount, the fee, who pays it (Form A: the older key;
//   Form B: your fee reserve), from (the older reserve) and to (yours). The device passphrase is asked after Confirm,
//   so no key is open during a user wait. `engine.sweep` opens the key and runs the recheck after that last wait; a
//   changed figure or form (`SweepChanged`) shows the review again with the new figures, and a refusal says why.
// - Only empty System accounts are priced: otherwise the notice says so and offers no Move. "Not yet" names the amount
//   to add to your fee reserve first; "blocked" waits for an earlier action's outcome, which the overview's next read
//   of the vault settles (its ledger sync; the guard reads the records as the page last synced them, R94); a move of
//   this session still awaiting its outcome ("pending") waits too, and is never called not priced.
// - The outcome: landed with its signature; not sent with the reason (the engine's `SweepNotSent` marks every failure
//   before the first send attempt); landed and failed; or not confirmed. The payer, account 0, is named by the form
//   (`describeSweep`). Nothing here writes storage except the pre-R48 address, through the engine and the session's
//   writer.
// - The session keeps every sent move's outcome as its one-shot 'sweep-outcome' notice (section 6: "A lock after the
//   send lets the confirmation finish and shows the outcome"). This notice takes it back (`session.takeSweepOutcome`)
//   when its own move resolves while it is still mounted and unlocked, since it shows the outcome itself; after a lock
//   during the confirmation, or once the page was left, `SweepOutcomeNotice` shows it on the door or the next overview.
// - The older address is labelled "older fee reserve", never like the top-up destination (section 6, "After the sweep").
// `OlderReservePointer` is the line a fee reserve refusal adds when an older reserve can be moved (section 6: "the panel
// points to the move"); it links to this notice (#older-reserve) on the overview.

import { PLAIN_ACTION_FEE, type SweepPlan, WorkerClientError } from '@thevault/sdk';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'wouter';
import { WRONG_NETWORK_SENTENCE, WrongNetwork } from '../deployment';
import { type Engine, EnvelopeUnreadable, type OlderReserve, SWEEP_PENDING_REASON, SweepChanged, SweepNotSent, SweepRefused, describe, describeSweep, sentSweepSignature } from '../engine';
import { RelayerError } from '../rpc';
import { isPassphraseRefusal, session, useSession } from '../session';
import { explorerBase, explorerUrl, shortSig } from '../util';
import { Address, type AddressKind, Amount, Button, Field, Kv, Notice, StatusLine } from './primitives';

/** The notice's anchor on the overview and in Settings; the pointer links to it. */
export const OLDER_RESERVE_ANCHOR = 'older-reserve';

/** The label of the older address wherever it is shown, so it never reads like the top-up destination (docs/18 section 6). */
export const OLDER_RESERVE_KIND: AddressKind = 'older fee reserve';

/** A failed read of the stored legacy envelope itself (`EnvelopeUnreadable`): whether the device holds one is not known. */
export const ENVELOPE_UNREADABLE_SENTENCE = 'This device may hold an older fee reserve key; it could not be read.';

/**
 * What the page last read of this device's older reserve: not yet, none stored, a reading, or a failed read. `envelope`
 * marks a failure of the envelope read itself (the device may hold none); any other failure came after the engine
 * found the envelope.
 */
export type OlderReserveState = { kind: 'reading' } | { kind: 'none' } | { kind: 'read'; reading: OlderReserve } | { kind: 'failed'; error: string; wrongNetwork: boolean; envelope: boolean };

export interface OlderReserveHandle {
  state: OlderReserveState;
  /** Read afresh (`engine.olderReserve()`, never cached); the reading, or null when none is stored or the read failed. */
  refresh: () => Promise<OlderReserve | null>;
  /** Show a reading the engine returned with a refusal (`SweepChanged`, `SweepRefused`) as the latest. */
  adopt: (reading: OlderReserve) => void;
}

function isLock(e: unknown): boolean {
  return e instanceof Error && e.message === 'locked';
}

/**
 * The older reserve's reading for a page, read at every mount (docs/18 section 6: "Home reads that balance at every
 * visit") unless `readAtMount` is false: the overview reads it itself after its own chain read, whose ledger sync
 * settles the guard's records (R94), so its notice never reads those records ahead of that sync.
 */
export function useOlderReserve(engine: Engine | null, { readAtMount = true }: { readAtMount?: boolean } = {}): OlderReserveHandle {
  const [state, setState] = useState<OlderReserveState>({ kind: 'reading' });
  // Only the latest call answers, so a slow earlier read never overwrites a newer one.
  const seq = useRef(0);
  const refresh = useCallback(async (): Promise<OlderReserve | null> => {
    if (!engine) return null;
    const n = ++seq.current;
    try {
      const reading = await engine.olderReserve();
      if (n === seq.current) setState(reading ? { kind: 'read', reading } : { kind: 'none' });
      return reading;
    } catch (e) {
      // A lock closes the engine under the read: the page is leaving, nothing to show.
      // an unreadable envelope keeps its storage error as the detail: the notice's own sentence says the rest
      if (n === seq.current && !isLock(e)) setState(e instanceof EnvelopeUnreadable ? { kind: 'failed', error: describe(e.cause), wrongNetwork: false, envelope: true } : { kind: 'failed', error: describe(e), wrongNetwork: e instanceof WrongNetwork, envelope: false });
      return null;
    }
  }, [engine]);
  const adopt = useCallback((reading: OlderReserve) => {
    seq.current++;
    setState({ kind: 'read', reading });
  }, []);
  useEffect(() => {
    if (readAtMount) void refresh();
  }, [refresh, readAtMount]);
  return { state, refresh, adopt };
}

/** A plan the sweep may send: Form A or B (docs/18 section 6, the table). */
type MovablePlan = Extract<SweepPlan, { form: 'A' | 'B' }>;

function movablePlanOf(reading: OlderReserve): MovablePlan | null {
  if (!reading.priced || reading.address === null || reading.lamports <= 0n) return null;
  return reading.plan.form === 'A' || reading.plan.form === 'B' ? reading.plan : null;
}

/** The reading when the table can move it now (priced, Form A or B), else null: what a fee reserve refusal points to. */
export function movableOf(reading: OlderReserve | null | undefined): OlderReserve | null {
  return reading && movablePlanOf(reading) ? reading : null;
}

/** The same, from a page's handle. */
export function movableReading(handle: OlderReserveHandle): OlderReserve | null {
  return handle.state.kind === 'read' ? movableOf(handle.state.reading) : null;
}

function Sol({ raw }: { raw: bigint }) {
  return <Amount raw={raw} decimals={9} symbol="SOL" maxFraction={9} />;
}

/**
 * The pointer a fee reserve refusal adds when this device's older reserve can be moved (docs/18 section 6; section 10,
 * the InsufficientGas pointer): `here` on a page that shows the notice itself (a same-page link), else a link to the
 * overview's notice.
 */
export function OlderReservePointer({ reading, here = false }: { reading: OlderReserve; here?: boolean }) {
  return (
    <p className="ink older-reserve-pointer">
      An older fee reserve on this device holds <Sol raw={reading.lamports} /> that can be moved into your fee reserve.{' '}
      {here ? <a href={`#${OLDER_RESERVE_ANCHOR}`}>Move it</a> : <Link href={`/vault#${OLDER_RESERVE_ANCHOR}`}>Move it from the overview</Link>}
    </p>
  );
}

const PASSPHRASE_SENTENCE = 'That passphrase does not open the older fee reserve key on this device, or the stored key is damaged. Nothing was sent.';

/** A move's outcome. `explains` marks a refusal whose sentence already says why there is no Move (the recheck's own reason), so the notice's line for that state is not repeated under it. */
type Outcome = { kind: 'landed'; signature: string; amount: bigint } | { kind: 'not-sent'; text: string; explains?: boolean } | { kind: 'failed'; text: string } | { kind: 'unconfirmed'; text: string };

/** A transfer that landed and failed: the chain charged its fee to account 0 (15,000 from the older key in Form A, 20,000 from your fee reserve in Form B). */
function landedFailedSentence(form: 'A' | 'B'): string {
  return `The transfer landed and failed; ${form === 'A' ? 'the older fee reserve' : 'your fee reserve'} paid its network fee and nothing else moved.`;
}

/** `describe`'s generic ConfirmFailed sentence: it adds nothing to `landedFailedSentence`, so it is not repeated. */
const GENERIC_CONFIRM_FAILED = 'The transaction landed but failed on chain.';

function sentence(s: string): string {
  const t = s.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

function isPassphrase(e: unknown): boolean {
  return isPassphraseRefusal(e) || (e instanceof WorkerClientError && e.code === 'BadPassphraseOrTampered');
}

/** The error a sweep refused with, unwrapped from the engine's `SweepNotSent` (raised before the first send attempt). */
function innerOf(e: unknown): unknown {
  return e instanceof SweepNotSent ? e.cause : e;
}

/** The sentence for a transfer that landed and failed, with the chain's own reason when `describeSweep` names one (5.1 B). */
function landedFailedText(detail: string | null, form: 'A' | 'B'): string {
  const base = landedFailedSentence(form);
  return detail && detail.trim() !== GENERIC_CONFIRM_FAILED ? `${base} ${sentence(detail)}` : base;
}

/**
 * A sweep of `form` that threw, in the user's words. Anything the engine wraps in `SweepNotSent` (a refusal, a changed
 * figure, the network check, the passphrase, the envelope, the worker, a read or the simulation, all before the first
 * send attempt) sent nothing; a failed confirmation landed, paid its fee and moved nothing else; anything else came
 * after a send attempt began, so it is not called "not sent". A transaction error names account 0 by the form.
 */
function outcomeOf(e: unknown, form: 'A' | 'B'): Outcome {
  const notSent = e instanceof SweepNotSent;
  const cause = innerOf(e);
  if (cause instanceof SweepRefused) return { kind: 'not-sent', text: cause.message, explains: true };
  // reached only when the new figures cannot be moved now: the notice's own line says why, so no "review again"
  if (cause instanceof SweepChanged) return { kind: 'not-sent', text: 'The figures changed since the review, so nothing was sent.' };
  if (isPassphrase(cause)) return { kind: 'not-sent', text: PASSPHRASE_SENTENCE };
  if (cause instanceof WrongNetwork) return { kind: 'not-sent', text: `${WRONG_NETWORK_SENTENCE} Nothing was sent.` };
  if (cause instanceof EnvelopeUnreadable) return { kind: 'not-sent', text: `${ENVELOPE_UNREADABLE_SENTENCE} Nothing was sent.` };
  if (cause instanceof WorkerClientError) return { kind: 'not-sent', text: `The older key could not prepare the transfer: ${cause.message}. Nothing was sent.` };
  // the simulation runs before every send attempt, whether or not the engine wrapped it
  if (cause instanceof RelayerError && cause.code === 'SimulationFailed') return { kind: 'not-sent', text: `${describeSweep(cause, form)} Nothing was sent.` };
  if (notSent) return { kind: 'not-sent', text: `${sentence(describeSweep(cause, form))} Nothing was sent.` };
  if (e instanceof RelayerError && e.code === 'ConfirmFailed') return { kind: 'failed', text: landedFailedText(describeSweep(e, form), form) };
  return { kind: 'unconfirmed', text: `${sentence(describeSweep(e, form))} If the transfer was sent, the balances here show it once the network has it; check again before moving again.` };
}

type Phase = 'idle' | 'reviewing' | 'review' | 'passphrase' | 'moving' | 'open-passphrase' | 'opening';

/**
 * The notice itself. `older` is the page's handle (`useOlderReserve`), shared with any pointer on the page; `onMoved`
 * runs after a move lands, so the page reads its fee reserve again.
 */
export function OlderReserveNotice({ engine, older, onMoved }: { engine: Engine; older: OlderReserveHandle; onMoved?: () => void }) {
  const { settings } = useSession();
  const [phase, setPhase] = useState<Phase>('idle');
  // The reading the review panel shows, with its plan: the figures `engine.sweep` must reproduce.
  const [reviewed, setReviewed] = useState<{ reading: OlderReserve; plan: MovablePlan } | null>(null);
  const [passphrase, setPassphrase] = useState('');
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  // Shown above a review that the recheck opened again with new figures.
  const [reviewNote, setReviewNote] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  // A pre-R48 key opened in this visit that holds nothing: said once, since the notice is otherwise not shown at 0.
  const [openedEmpty, setOpenedEmpty] = useState(false);
  const busy = useRef(false);
  const anchor = useRef<HTMLDivElement>(null);
  const scrolled = useRef(false);
  const passphraseField = useRef<HTMLInputElement>(null);
  // Confirm unmounts with the review's buttons: the passphrase field takes focus, so it does not fall to the body.
  useEffect(() => {
    if (phase === 'passphrase') passphraseField.current?.focus();
  }, [phase]);
  // Whether this notice is still mounted when its move resolves (StrictMode's remount sets it again).
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  /**
   * This notice shows the outcome of the move with `signature` itself, so the session's copy of it goes (docs/18
   * section 6). Not once the notice has unmounted (the page was left) or the session has locked: the session's copy is
   * then the only one, for the door or the next overview.
   */
  const takeOutcome = (signature: string | null): void => {
    if (signature !== null && mounted.current && session.get().state === 'unlocked') session.takeSweepOutcome(signature);
  };

  const state = older.state;
  const reading = state.kind === 'read' ? state.reading : null;
  const visible = state.kind === 'failed' || outcome !== null || openedEmpty || (reading !== null && (reading.address === null || reading.lamports > 0n));

  // Reached by the pointer's link from another page (/vault#older-reserve): the router does not scroll to a fragment,
  // and the notice appears only after its read, so it scrolls itself into view once.
  useEffect(() => {
    if (!visible || scrolled.current || window.location.hash !== `#${OLDER_RESERVE_ANCHOR}`) return;
    scrolled.current = true;
    anchor.current?.scrollIntoView({ block: 'start' });
  }, [visible]);

  const reset = () => {
    setPhase('idle');
    setReviewed(null);
    setPassphrase('');
    setReviewNote(null);
  };

  /** Move: everything is read again, and the review shows those figures; Confirm is offered only on that reading. */
  const move = async () => {
    if (busy.current) return;
    busy.current = true;
    setOutcome(null);
    setOpenedEmpty(false);
    setReviewNote(null);
    setPhase('reviewing');
    try {
      const fresh = await older.refresh();
      const plan = fresh ? movablePlanOf(fresh) : null;
      if (fresh && plan) {
        setReviewed({ reading: fresh, plan });
        setPhase('review');
      } else {
        // the notice's own lines now say why (not priced, blocked, not yet, empty, or the failed read)
        reset();
      }
    } finally {
      busy.current = false;
    }
  };

  /** After the passphrase: `engine.sweep` opens the key, rechecks after this last wait and sends only the reviewed figures. */
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (busy.current || !reviewed || !passphrase) return;
    busy.current = true;
    const p = passphrase;
    // the field is cleared at once; a JavaScript string cannot be zeroed
    setPassphrase('');
    setPhase('moving');
    const { plan } = reviewed;
    try {
      const result = await engine.sweep(p, { form: plan.form, amount: plan.amount, fee: plan.fee });
      takeOutcome(result.signature);
      setOutcome({ kind: 'landed', signature: result.signature, amount: plan.amount });
      reset();
      void older.refresh();
      onMoved?.();
    } catch (err) {
      // the refusal itself, whether or not the engine wrapped it in SweepNotSent
      const inner = innerOf(err);
      if (isLock(inner)) {
        // a lock closed the engine before the send: the page is leaving, and nothing was sent
        reset();
        return;
      }
      if (inner instanceof SweepChanged) {
        older.adopt(inner.reading);
        const next = movablePlanOf(inner.reading);
        if (next) {
          // The figures moved during a wait: the review again, with the new figures; its Confirm asks for the passphrase again.
          setReviewed({ reading: inner.reading, plan: next });
          setReviewNote(inner.message);
          setOutcome(null);
          setPhase('review');
          return;
        }
      } else if (inner instanceof SweepRefused && inner.reading) {
        older.adopt(inner.reading);
      }
      // after a send attempt the engine reported the outcome to the session too; this line shows it, so that copy goes
      takeOutcome(sentSweepSignature(err));
      setOutcome(outcomeOf(err, plan.form));
      reset();
      // anything but the recheck's own answer: read again (a failure after the send may have moved it)
      if (!(inner instanceof SweepChanged || inner instanceof SweepRefused)) void older.refresh();
    } finally {
      busy.current = false;
    }
  };

  /** Pre-R48: open the key once to learn its address (recorded by the engine through the session's writer), then read. */
  const open = async (e: FormEvent) => {
    e.preventDefault();
    if (busy.current || !passphrase) return;
    busy.current = true;
    const p = passphrase;
    setPassphrase('');
    setOpenError(null);
    setPhase('opening');
    try {
      await engine.openOlderReserve(p);
      const fresh = await older.refresh();
      setOpenedEmpty(fresh !== null && fresh.address !== null && fresh.lamports === 0n);
      reset();
    } catch (err) {
      if (!isLock(err))
        setOpenError(
          isPassphrase(err)
            ? 'That passphrase does not open the older fee reserve key on this device, or the stored key is damaged. Nothing was recorded.'
            : err instanceof EnvelopeUnreadable
              ? `${ENVELOPE_UNREADABLE_SENTENCE} Nothing was recorded.`
              : describe(err),
        );
      reset();
    } finally {
      busy.current = false;
    }
  };

  const checkAgain = () => {
    setOutcome(null);
    setOpenedEmpty(false);
    void older.refresh();
  };

  if (!visible) return null;

  const txBase = explorerBase(settings.rpcUrl, 'tx');
  const outcomeLine = outcome ? <OutcomeLine outcome={outcome} txBase={txBase} /> : null;

  let body;
  if (state.kind === 'failed') {
    // The reading error, never a claim the read did not establish: a failed envelope read leaves open whether this
    // device holds an older key at all (docs/18 section 6); any later failure came after the engine found the envelope.
    body = (
      <>
        <p className="ink">
          {state.envelope ? `${ENVELOPE_UNREADABLE_SENTENCE} Details: ${sentence(state.error)}` : `The balance of the older fee reserve key on this device could not be read${state.wrongNetwork ? '.' : `: ${sentence(state.error)}`}`}
        </p>
        {state.wrongNetwork ? (
          <StatusLine state="failed" word="Wrong network.">
            {WRONG_NETWORK_SENTENCE}
          </StatusLine>
        ) : null}
        {outcomeLine}
        <div className="actions">
          <Button small variant="ghost" onClick={checkAgain}>
            Check again
          </Button>
        </div>
      </>
    );
  } else if (reading && reading.address === null) {
    // Set up before R48 recorded the older address (docs/18 section 6): its balance cannot be read until it is opened.
    body = (
      <>
        <p className="ink">This device holds an older fee reserve key whose address was not recorded. Open it to see its balance.</p>
        {openError ? (
          <StatusLine state="failed" word="Not opened.">
            {openError}
          </StatusLine>
        ) : null}
        {phase === 'open-passphrase' || phase === 'opening' ? (
          <form onSubmit={open} className="stack tight">
            <Field id="older-reserve-open-passphrase" label="Device passphrase" help="It opens the older key to read its address, which this device then records. Nothing is sent.">
              <input id="older-reserve-open-passphrase" className="input" type="password" autoComplete="current-password" value={passphrase} onChange={(ev) => setPassphrase(ev.target.value)} />
            </Field>
            <div className="actions">
              <Button type="submit" small disabled={!passphrase || phase === 'opening'} working={phase === 'opening'}>
                {phase === 'opening' ? 'Opening' : 'Open it'}
              </Button>
              <Button small variant="ghost" onClick={reset} disabled={phase === 'opening'}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="actions">
            <Button
              small
              onClick={() => {
                setOpenError(null);
                setPhase('open-passphrase');
              }}
            >
              Open it to see its balance
            </Button>
          </div>
        )}
      </>
    );
  } else if (reading && reading.lamports === 0n) {
    // Moved, or opened and empty: the notice clears at the next visit; the outcome stays while this page is open.
    body = (
      <>
        {openedEmpty && reading.address ? (
          <StatusLine state="neutral" word="Opened.">
            The older fee reserve key on this device, at <Address value={reading.address} kind={OLDER_RESERVE_KIND} />, holds nothing, so there is nothing to move.
          </StatusLine>
        ) : null}
        {outcomeLine}
        <div className="actions">
          <Button
            small
            variant="ghost"
            onClick={() => {
              setOutcome(null);
              setOpenedEmpty(false);
            }}
          >
            Done
          </Button>
        </div>
      </>
    );
  } else if (reading) {
    const plan = reading.plan;
    const inFlow = phase === 'review' || phase === 'passphrase' || phase === 'moving';
    body = (
      <>
        <p className="ink">
          This device has an older fee reserve with <Sol raw={reading.lamports} />. Move it to your fee reserve.
        </p>
        {inFlow && reviewed ? (
          <ReviewPanel engine={engine} reviewed={reviewed} note={reviewNote} />
        ) : (
          <>
            {reading.address ? <Kv rows={[['Older fee reserve', <Address key="older" value={reading.address} kind={OLDER_RESERVE_KIND} />]]} /> : null}
            {outcomeLine}
            {outcome?.kind === 'not-sent' && outcome.explains ? null : <PlanLine reading={reading} />}
          </>
        )}
        {phase === 'review' ? (
          <div className="actions">
            <Button small onClick={() => setPhase('passphrase')}>
              Confirm
            </Button>
            <Button small variant="ghost" onClick={reset}>
              Cancel
            </Button>
          </div>
        ) : phase === 'passphrase' ? (
          // asked after Confirm (docs/18 section 6), so no key is open during the review
          <form onSubmit={send} className="stack tight">
            <Field id="older-reserve-passphrase" label="Device passphrase" help="It opens the older key for this move only. The figures are checked again before anything is sent.">
              <input ref={passphraseField} id="older-reserve-passphrase" className="input" type="password" autoComplete="current-password" value={passphrase} onChange={(ev) => setPassphrase(ev.target.value)} />
            </Field>
            <div className="actions">
              <Button type="submit" small disabled={!passphrase}>
                Move
              </Button>
              <Button small variant="ghost" onClick={reset}>
                Cancel
              </Button>
            </div>
          </form>
        ) : phase === 'moving' ? (
          <StatusLine state="pending" word="Moving.">
            Opening the older key, checking the network, both balances and the account minimum again, then sending the transfer.
          </StatusLine>
        ) : phase === 'reviewing' ? (
          <StatusLine state="pending" word="Reading.">
            Checking the network, then reading both balances and the account minimum.
          </StatusLine>
        ) : (
          <div className="actions">
            {movablePlanOf(reading) ? (
              <Button small onClick={() => void move()}>
                Move
              </Button>
            ) : null}
            {!movablePlanOf(reading) ? (
              <Button small variant="ghost" onClick={checkAgain}>
                Check again
              </Button>
            ) : null}
            {reading.priced && plan.form === 'not-yet' ? (
              <Link href="/deposit" className="button ghost small">
                Add SOL on the Deposit page
              </Link>
            ) : null}
          </div>
        )}
      </>
    );
  }

  return (
    <div id={OLDER_RESERVE_ANCHOR} ref={anchor} role="region" aria-label="Older fee reserve" className="older-reserve">
      <Notice label="Older fee reserve" tone="warn">
        <div className="stack tight">{body}</div>
      </Notice>
    </div>
  );
}

/** Why there is no Move, or nothing when there is one (docs/18 section 6: a move awaiting its outcome, not priced, blocked, not yet). */
function PlanLine({ reading }: { reading: OlderReserve }) {
  const plan = reading.plan;
  // A move of this session awaiting its outcome is the only reason (`pending`): it resolves on its own, so it waits.
  if (reading.pending) {
    return (
      <StatusLine state="neutral" word="Waiting.">
        {reading.unpricedReason ?? SWEEP_PENDING_REASON}
      </StatusLine>
    );
  }
  if (!reading.priced) {
    return (
      <StatusLine state="failed" word="Not priced.">
        {reading.unpricedReason ?? 'An account the move would touch is not an empty System account, so the app does not price a move. Nothing is sent.'}
      </StatusLine>
    );
  }
  if (plan.form === 'blocked') {
    return (
      <StatusLine state="neutral" word="Waiting.">
        An earlier action&rsquo;s outcome is not known yet, and the older fee reserve may still be paying for it. The move waits until the overview&rsquo;s next read of the vault settles that action: reload the overview, or open it again.
      </StatusLine>
    );
  }
  if (plan.form === 'not-yet') {
    // The payer rule, not the fee, is why (the table): Form A leaves the older key at least F after its fee, so it
    // needs L >= F + 15,000; below that Form B's 20,000 comes from your fee reserve, which must keep F too.
    return (
      <StatusLine state="neutral" word="Not yet.">
        For it to pay the fee itself it would need at least <Sol raw={reading.minimum + PLAIN_ACTION_FEE} /> (the network&rsquo;s account minimum plus the fee), so your fee reserve pays the fee instead. Add at least <Sol raw={plan.add} /> to your fee reserve first, from any wallet, then move. Your fee reserve holds <Sol raw={reading.current} />.
      </StatusLine>
    );
  }
  return null;
}

/** The one review panel (docs/18 section 6): the amount, the fee and who pays it, from (old) and to (yours). */
function ReviewPanel({ engine, reviewed, note }: { engine: Engine; reviewed: { reading: OlderReserve; plan: MovablePlan }; note: string | null }) {
  const { reading, plan } = reviewed;
  return (
    <>
      {note ? (
        <StatusLine state="neutral" word="Check again.">
          {note}
        </StatusLine>
      ) : null}
      <div className="review">
        <Kv
          rows={[
            ['Amount', <Sol key="amount" raw={plan.amount} />],
            ['Network fee', <Sol key="fee" raw={plan.fee} />],
            ['Fee paid by', plan.form === 'A' ? 'the older key' : 'your fee reserve'],
            ['From', <Address key="from" value={reading.address!} kind={OLDER_RESERVE_KIND} />],
            ['To', <Address key="to" value={engine.relayerAddress} kind="fee reserve" />],
          ]}
        />
      </div>
      <p className="ink">
        {plan.form === 'A' ? 'The older key pays the fee from what it holds and ends empty.' : 'Your fee reserve pays the fee, and the older key signs too; it ends empty.'} From is the older fee reserve on this device; To is
        your fee reserve, derived from your 24 words. No vault key is used.
      </p>
    </>
  );
}

function OutcomeLine({ outcome, txBase }: { outcome: Outcome; txBase: string | null }) {
  if (outcome.kind === 'landed') {
    const link = explorerUrl(txBase, outcome.signature);
    return (
      <StatusLine state="landed" word="Landed.">
        <Sol raw={outcome.amount} /> moved into your fee reserve. Transaction{' '}
        {link ? (
          <a href={link} target="_blank" rel="noreferrer">
            {shortSig(outcome.signature)} ↗
          </a>
        ) : (
          shortSig(outcome.signature)
        )}
        .
      </StatusLine>
    );
  }
  const word = outcome.kind === 'not-sent' ? 'Not sent.' : outcome.kind === 'failed' ? 'Failed.' : 'Not confirmed.';
  return (
    <StatusLine state="failed" word={word}>
      {outcome.text}
    </StatusLine>
  );
}

/**
 * A move's outcome as the session keeps it (its one-shot 'sweep-outcome' notice, from the engine's `SweepOutcome`)
 * when the notice above could not show it: the confirmation finished after a lock closed the engine (`afterLock`;
 * docs/18 section 6: "A lock after the send lets the confirmation finish and shows the outcome"), or after the page
 * that pressed Move was left. `landed` is true, with `detail` null, once the transfer confirmed; `failed` is true for a
 * transfer that landed and failed on chain; otherwise it was not confirmed. `detail` is the engine's sentence for what
 * is known, naming the payer by the form.
 */
export interface SweepOutcomeShown {
  signature: string;
  amount: bigint;
  form: 'A' | 'B';
  landed: boolean;
  failed: boolean;
  detail: string | null;
  afterLock: boolean;
}

/** That outcome, once, on the door and on the next overview; Dismiss clears it (`session.dismissNotice`), as does Forget. */
export function SweepOutcomeNotice({ outcome }: { outcome: SweepOutcomeShown }) {
  const { settings } = useSession();
  const link = explorerUrl(explorerBase(settings.rpcUrl, 'tx'), outcome.signature);
  const tx = link ? (
    <a href={link} target="_blank" rel="noreferrer">
      {shortSig(outcome.signature)} ↗
    </a>
  ) : (
    shortSig(outcome.signature)
  );
  let line;
  if (outcome.landed) {
    line = (
      <StatusLine state="landed" word="Landed.">
        The move landed: <Sol raw={outcome.amount} />, transaction {tx}. It moved into your fee reserve{outcome.afterLock ? '; its confirmation finished after this device locked' : ''}.
      </StatusLine>
    );
  } else if (outcome.failed) {
    // The engine's detail names the payer, account 0, by the form: Form A the older key, Form B your fee reserve (the table)
    line = (
      <StatusLine state="failed" word="Failed.">
        The move landed and failed, transaction {tx}. {outcome.detail ? sentence(outcome.detail) : `${outcome.form === 'A' ? 'The older fee reserve' : 'Your fee reserve'} paid its network fee and nothing else moved.`}
      </StatusLine>
    );
  } else {
    line = (
      <StatusLine state="neutral" word="Not confirmed.">
        The move was sent{outcome.afterLock ? ' before this device locked' : ''}, transaction {tx}, and may not have arrived.{outcome.detail ? ` ${sentence(outcome.detail)}` : ''} Check the older fee reserve&rsquo;s balance on the overview before moving again.
      </StatusLine>
    );
  }
  return (
    <div role="region" aria-label="Older reserve move" className="older-reserve-outcome">
      <Notice label="Older reserve move" tone={outcome.landed ? undefined : 'warn'}>
        <div className="stack tight">
          {line}
          <div className="actions">
            <Button small variant="ghost" onClick={() => session.dismissNotice()}>
              Dismiss
            </Button>
          </div>
        </div>
      </Notice>
    </div>
  );
}
